import React, { useState, useContext, useMemo } from "react";
import { login } from "../api";
import { useNavigate, Link } from "react-router-dom";
import { ThemeContext } from "../App";
import logo from "../assets/astratrade-logo.svg";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useContext(ThemeContext);

  const classes = useMemo(() => {
    const baseBackground =
      theme === "dark"
        ? "bg-slate-950 text-white"
        : "bg-slate-50 text-gray-900";
    const card =
      theme === "dark"
        ? "bg-gray-800 text-white"
        : "bg-white text-gray-900 border border-gray-200";
    const input =
      theme === "dark"
        ? "w-full p-2 mb-4 rounded bg-gray-900 border border-gray-600 text-white placeholder-gray-400"
        : "w-full p-2 mb-4 rounded bg-white border border-gray-300 text-gray-900 placeholder-gray-500";
    return { baseBackground, card, input };
  }, [theme]);

  const handleLogin = async () => {
    if (loading) return;
    const normalizedUsername = username.trim();
    const normalizedPassword = password.trim();

    if (!normalizedUsername || !normalizedPassword) {
      setError("Please enter both username and password");
      return;
    }

    try {
      setError("");
      setLoading(true);
      const res = await login(normalizedUsername, normalizedPassword);

      // Navigate based on role
      if (res.role === "admin") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/user", { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleLogin();
    }
  };

  return (
    <div
      className={`flex justify-center items-center min-h-screen transition-colors duration-200 ${classes.baseBackground}`}
    >
      <div
        className={`p-8 rounded-lg shadow-lg w-96 transition-colors duration-200 ${classes.card}`}
      >
        <div className="flex justify-center mb-6">
          <img src={logo} alt="AstraTrade logo" className="h-16 w-auto" />
        </div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Login</h2>
          <button
            className="text-sm px-3 py-1 rounded border border-current"
            onClick={toggleTheme}
          >
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        {error && (
          <div className="bg-red-500 text-white p-2 rounded mb-4 text-center">
            {error}
          </div>
        )}

        <input
          className={classes.input}
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <input
          type="password"
          className={classes.input}
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          className={`w-full p-2 rounded font-bold text-white mb-4 ${
            loading
              ? "bg-blue-400 cursor-not-allowed"
              : "bg-blue-500 hover:bg-blue-600"
          }`}
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? "Signing in..." : "Login"}
        </button>

        <div className="text-center">
          <Link to="/signup" className="text-blue-500 hover:underline">
            Don't have an account? Sign up here
          </Link>
        </div>
        <div className="text-center mt-2">
          <Link
            to="/forgot-password"
            className="text-sm text-blue-500 hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <div className="text-center mt-4 space-x-3">
          <Link to="/terms" className="text-xs text-blue-400 hover:underline">
            Terms and Conditions
          </Link>
          <span className="opacity-50 text-xs">•</span>
          <Link to="/privacy" className="text-xs text-blue-400 hover:underline">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
